import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { API_URL } from '@core/config/api-url.token';
import { SUPPRESS_ERROR_TOAST_EXCEPT_SERVICE_UNAVAILABLE } from '@core/http/http-context-tokens';
import { API_ENDPOINTS } from '@shared/constants/api-endpoints.const';
import { Observable } from 'rxjs';
import { LocalClientDto } from '../../dtos/local-client.dto';
import { RoleDto } from '../../dtos/role.dto';

// No `Password`: this feature only ever creates tutor accounts through the invitation flow, which
// the backend selects by the absence of that field.
export interface CreateLocalClientPayload {
  readonly Username: string;
  readonly FirstName: string;
  readonly LastName: string;
  // `null` resolves to the `Client` role server-side (`LocalClientsService.ResolveRoleAsync`), which
  // is what the "give app access" flows want without a roles round-trip.
  readonly RoleId: number | null;
  readonly CreateInProvider: boolean;
  readonly ProviderProfile?: Record<string, unknown>;
  // Links the new account to an already-existing ProVet client instead of creating a second one.
  readonly LinkedProviderClientExternalId?: string;
  // Opt-in: when the email already belongs to a tutor account at ANOTHER clinic, the backend gives
  // that account a membership here (through `LinkedProviderClientExternalId`) instead of a 400.
  readonly LinkExistingAccount?: boolean;
}

export interface ClientAppInvitationPayload {
  readonly Username: string;
  readonly FirstName: string;
  readonly LastName: string;
  readonly LinkedProviderClientExternalId: string;
}

@Injectable()
export class LocalClientsRemoteDatasource {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(API_URL);

  public list(organizationId: number): Observable<readonly LocalClientDto[]> {
    return this.http.get<readonly LocalClientDto[]>(
      `${this.apiUrl}${API_ENDPOINTS.organizationClients.list(organizationId)}`,
      { params: { accountRealm: 'pet_owner' } },
    );
  }

  // The create drawer renders every failure of this call as its own banner (naming a taken email
  // specifically), so the generic "revisa los datos" toast would only contradict it.
  public create(organizationId: number, payload: CreateLocalClientPayload): Observable<LocalClientDto> {
    return this.http.post<LocalClientDto>(
      `${this.apiUrl}${API_ENDPOINTS.organizationClients.list(organizationId)}`,
      { ...payload, AccountRealm: 'pet_owner', RoleId: null },
      { context: new HttpContext().set(SUPPRESS_ERROR_TOAST_EXCEPT_SERVICE_UNAVAILABLE, true) },
    );
  }

  public resendInvitation(organizationId: number, clientId: number): Observable<void> {
    return this.http.post<void>(
      `${this.apiUrl}${API_ENDPOINTS.organizationClients.resendAppInvitation(organizationId, clientId)}`,
      null,
    );
  }

  public grantAppAccess(organizationId: number, payload: ClientAppInvitationPayload): Observable<LocalClientDto> {
    return this.http.post<LocalClientDto>(
      `${this.apiUrl}${API_ENDPOINTS.organizationClients.appInvitations(organizationId)}`,
      payload,
    );
  }

  public listRoles(): Observable<readonly RoleDto[]> {
    return this.http.get<readonly RoleDto[]>(`${this.apiUrl}${API_ENDPOINTS.roles.list}`);
  }
}
